import React, { useMemo } from 'react'

import { AppSvg, FormattedCurrency, FormattedHTMLMessage, MainSelect, newIcons, SelectOption } from '@web/walter-ui-kit'
import { GetCard } from '@web/walter-lib/api'
import s from './s.module.css'
import { lang } from './lang'
import { selectLang } from '~uikitLang'
import { useCardBalance, useCards, useProducts } from '~api'
import CardImage from '~pages/CardsPage/components/CardImage'

interface Props {
    onSelect: (newValue: string | null) => void
    value: string | null
    currentCard?: GetCard
}

function Select({ onSelect, value, currentCard }: Props) {
    const { data: cards } = useCards()
    const { data: products } = useProducts()

    const options = useMemo(() => {
        const opts =
            cards?.cards?.reduce((acc, card) => {
                if (card?.status === 'ACTIVE') {
                    acc.push({
                        value: card?.id,
                        label: `·· ${card?.masked_number || '****'}`,
                        icon: (
                            <div className={s.img}>
                                <CardImage designId={card?.card_design?.id} />
                            </div>
                        ),
                    })
                }

                return acc
            }, [] as SelectOption[]) || []

        if (products?.products && products?.products?.length > 0) {
            opts.push({
                value: 'newCard',
                label: (
                    <div className={s.newCard}>
                        <AppSvg path="new-icons" svgName={newIcons.ic24Plus} size={16} />
                        <FormattedHTMLMessage {...lang.getNew} />
                    </div>
                ),
            })
        }

        return opts
    }, [cards?.cards, products?.products])

    const { data: balance } = useCardBalance({ id: currentCard?.id || '' })

    const currentOption = useMemo(() => {
        return options?.find(option => option.value === value)
    }, [options, value])

    return (
        <MainSelect
            inPortal
            isSearchable={false}
            options={options}
            onChange={onSelect}
            listHeight={240}
            valueContainer={
                <div className={s.valueContainer}>
                    <div className={s.valueContainerLeft}>
                        <CardImage designId={currentCard?.card_design?.id} className={s.img} />
                        <div className={s.optionValue}>
                            <FormattedHTMLMessage {...lang.toCard} className={s.toCard} />
                            <span className={s.toCardValue} data-pointer="card_select_value">
                                {currentOption?.label}
                            </span>
                        </div>
                    </div>
                    <div className={s.selectAmount}>
                        {balance?.currency && (
                            <FormattedCurrency
                                maxPrecision={2}
                                value={balance?.available_balance || 0}
                                currency={balance?.currency}
                                dataPointer="balance"
                            />
                        )}
                    </div>
                </div>
            }
            controlClassName={s.control}
            value={value}
            locales={{ ...selectLang }}
            fullWidth
            dataPointer="card_select"
        />
    )
}

export default Select
